import { Component, OnDestroy, OnInit, AfterViewInit } from "@angular/core";
import { WebCompany, WebCustomer, WebCustomerAgingVariables, WebRecentActivity } from "src/app/models";
import { Subscription } from "rxjs";
import { ApsService, CompanyService, CustomerService, SharedService } from "src/app/services";
import { HistoryService } from "src/app/services/history.service";
import { MatDialog } from "@angular/material/dialog";
import { RequestServiceDialogComponent } from "src/app/pages/summary/request-service-dialog/request-service-dialog.component";
import { RequestDeliveryDialogComponent } from "src/app/pages/summary/request-delivery-dialog/request-delivery-dialog.component";
import { MakeAPaymentDialogComponent } from "src/app/pages/aps/make-a-payment-dialog/make-a-payment-dialog.component";
import { CustomerAgingDialogComponent } from "src/app/pages/summary/customer-aging-dialog/customer-aging-dialog.component";
import { BreakpointObserver } from "@angular/cdk/layout";
import { SpinnerService } from "src/app/services/spinner.service";
import { AlertDialogComponent } from "src/app/components/alert-dialog/alert-dialog.component";
import { SessionDataService } from "src/app/services/session-data.service";
import { filter } from "rxjs/operators";

@Component({
    selector: 'app-summary-header',
    templateUrl: './summary-header.component.html',
    styleUrls: ['./summary-header.component.scss']
})
export class SummaryHeaderComponent implements OnInit, OnDestroy
{

    title: string = "Summary";

    isMobile: boolean;
    isEmployee: boolean;
    messageForCODCustomer: string = "";
    isCODCustomer: boolean;
    balanceDetailColor: string = "";

    isAccountPastDue: boolean;
    isLegalCustomer: boolean;

    announcement: string;
    showAnnouncement: boolean;
    isDeliveryForCODRestricted: boolean;

    customer: WebCustomer;
    customerSubscription: Subscription;

    webSettingsSubscription: Subscription;

    customerAging: WebCustomerAgingVariables;
    customerAgingSubscription: Subscription;

    company: WebCompany;
    companySubscription: Subscription;

    activities: WebRecentActivity[];
    activitiesSubscription: Subscription;

    constructor(
        private customerService: CustomerService,
        private companyService: CompanyService,
        private historyService: HistoryService,
        private apsService: ApsService,
        private sharedService: SharedService,
        private sessionDataService: SessionDataService,
        public dialog: MatDialog,
        private breakpointObserver: BreakpointObserver,
        public spinnerService: SpinnerService,

    ) { }

    ngOnInit(): void
    {
        this.webSettingsSubscription = this.companyService.webSettings.subscribe(s =>
        {
            if (s != null)
            {
                this.messageForCODCustomer = s.DeliveryForCODFromWebMessage;
                if (s.RestrictDeliveryForCODFromWeb) 
                {
                    this.isDeliveryForCODRestricted = true;
                }
            }
        });

        this.isEmployee = this.sessionDataService.IsEmployee;

        this.breakpointObserver.observe("(max-width: 1127px)").subscribe(result =>
        {
            this.isMobile = result.matches;
        });

        this.customerSubscription = this.customerService.customer.subscribe(customer =>
        {
            this.customer = customer;
            this.isCODCustomer = customer.AcctStat === 6 || customer.AcctStatSameAsCod;
        });


        this.customerAgingSubscription = this.customerService.customerAging.subscribe(aging =>
        {
            this.customerAging = aging;
            this.isAccountPastDue = aging.IsAccountPastDue;
            this.balanceDetailColor = aging.DisplayBalanceColorHex;

            if (this.balanceDetailColor === "#FFFFFF" && !this.sharedService.isDark)
            {
                this.balanceDetailColor = "";
            }
        });

        this.companySubscription = this.companyService.company.subscribe(company =>
        {
            this.company = company;
        });
    }

    ngOnDestroy(): void
    {
        this.customerSubscription?.unsubscribe();
        this.customerAgingSubscription?.unsubscribe();
        this.companySubscription?.unsubscribe();
        this.webSettingsSubscription?.unsubscribe();
    }

    openDialogSWO(): void
    {
        if (this.customer.AcctStatAllowTicketOrSwo || this.isEmployee)
        {
            this.dialog.open(RequestServiceDialogComponent, {
                width: "400px",
                maxWidth: "90%",
                restoreFocus: false,
                disableClose: true,
            });
        }
        else
        {
            this.dialog.open(AlertDialogComponent, {
                id: "swo-disallow-alert",
                panelClass: "form-field-less-than-180",
                maxWidth: "600px",
                maxHeight: "500px",
                data: {
                    title: "Alert",
                    message: "Please contact our office to make a service request. \n Phone: "
                        + this.company.Phone, buttonText: "Close"
                }
            });
        }
    }

    public openDialogDelivery(): void
    {
        if (!this.customer.AcctStatAllowTicketOrSwo || (this.isCODCustomer && this.isDeliveryForCODRestricted && !this.isEmployee))
        {
            const message =
                !SharedService.IsNullOrEmpty(this.messageForCODCustomer)
                    ? this.messageForCODCustomer
                    : "Unable to process your delivery request, please call the office at " +
                    this.company.Phone;
            this.dialog.open(AlertDialogComponent, {
                id: "delivery-restricted-alert",
                panelClass: "form-field-less-than-180",
                maxWidth: "600px",
                maxHeight: "500px",
                data: {
                    title: "Alert",
                    message: message, buttonText: "Close"
                }
            });
        }
        else if (this.customer.AcctStatAllowTicketOrSwo || (!this.isDeliveryForCODRestricted) || this.isEmployee)
        {
            this.dialog.open(RequestDeliveryDialogComponent, {
                width: "400px",
                maxWidth: "90%",
                restoreFocus: false,
                disableClose: true,
            });
        }
    }

    openCustomerAgingDialog(event, unpaidFinanceChg: number): void
    {
        let topPos;
        let leftPos;

        if (!this.isMobile)
        {
            const myOffset = this.getPosition(event); // gets position of clicked element
            topPos = ((myOffset.offsetTop) + 20).toString() + "px";
            leftPos = ((myOffset.offsetLeft) - 300).toString() + "px";
        }

        this.dialog.open(CustomerAgingDialogComponent, {
            maxWidth: "90%",
            data: unpaidFinanceChg,
            restoreFocus: false,
            position: { top: topPos, left: leftPos }
        });
    }

    public handleMakeAPaymentClicked(): void
    {
        this.dialog.open(MakeAPaymentDialogComponent, {
            panelClass: "form-field-less-than-180",
            minHeight: "400px",
            maxHeight: "90%",
            maxWidth: "90%",
            disableClose: true
        });
        this.spinnerService.hide();
    }
  
    getPosition(event)
    {
        let offsetLeft = 0;
        let offsetTop = 0;
        let el = event.target;

        while (el)
        {
            offsetLeft += el.offsetLeft;
            offsetTop += el.offsetTop;
            el = el.offsetParent;
        }
        return { offsetTop, offsetLeft };
    }
}